import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";

const EMPTY = {
  company_name: "", poc_name: "", poc_email: "", poc_phone: "",
  engagement_scope: "", contract_start_date: "", project_end_date: "", status: "Onboarding",
};

export default function ClientFormDialog({ open, onOpenChange, onCreated }) {
  const [form, setForm] = useState(EMPTY);
  const [setupPortal, setSetupPortal] = useState(false);
  const [portalEmail, setPortalEmail] = useState("");
  const [portalPassword, setPortalPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setSetupPortal(false);
      setPortalEmail("");
      setPortalPassword("");
    }
  }, [open]);

  const set = (k) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [k]: value }));
    if (k === "poc_email" && !portalEmail) setPortalEmail(value);
  };

  const handleSubmit = async () => {
    if (!form.company_name || !form.poc_name || !form.engagement_scope || !form.contract_start_date) {
      toast.error("Company name, POC, scope and start date are required");
      return;
    }
    if (setupPortal && (!portalEmail.trim() || !portalPassword.trim())) {
      toast.error("Portal login email and password are required to set up client access");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, poc_email: form.poc_email || null, project_end_date: form.project_end_date || null };
      const res = await api.post("/clients", payload);
      if (setupPortal) {
        try {
          await api.post(`/clients/${res.data.id}/portal-access`, { email: portalEmail.trim(), password: portalPassword.trim() });
          toast.success("Client account created & portal access granted");
        } catch (portalErr) {
          toast.error(`Client created, but portal access failed: ${formatApiErrorDetail(portalErr.response?.data?.detail)}`);
        }
      } else {
        toast.success("Client account created");
      }
      onOpenChange(false);
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg" data-testid="client-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">Add Client Account</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Company Name</Label>
              <Input data-testid="client-form-company-input" value={form.company_name} onChange={set("company_name")} placeholder="e.g. Greenfield Agro" />
            </div>
            <div className="space-y-1.5">
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="client-form-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Onboarding">Onboarding</SelectItem>
                  <SelectItem value="Active">Active</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>POC Name</Label>
              <Input data-testid="client-form-poc-name-input" value={form.poc_name} onChange={set("poc_name")} placeholder="Point of contact" />
            </div>
            <div className="space-y-1.5">
              <Label>POC Email</Label>
              <Input data-testid="client-form-poc-email-input" type="email" value={form.poc_email} onChange={set("poc_email")} placeholder="poc@company.com" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>POC Phone</Label>
              <Input data-testid="client-form-poc-phone-input" value={form.poc_phone} onChange={set("poc_phone")} placeholder="+91 XXXXX XXXXX" />
            </div>
            <div className="space-y-1.5" />
          </div>
          <div className="space-y-1.5">
            <Label>Engagement Scope</Label>
            <Textarea data-testid="client-form-scope-input" value={form.engagement_scope} onChange={set("engagement_scope")} rows={2} placeholder="Describe project scope" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label>Contract Start Date</Label>
              <Input data-testid="client-form-start-date-input" type="date" value={form.contract_start_date} onChange={set("contract_start_date")} />
            </div>
            <div className="space-y-1.5">
              <Label>Project End Date</Label>
              <Input data-testid="client-form-end-date-input" type="date" value={form.project_end_date} onChange={set("project_end_date")} />
            </div>
          </div>

          <div className="border border-[#E2E8F0] rounded-md p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <KeyRound className="h-4 w-4 text-[#00823B]" />
                <div>
                  <p className="text-sm font-medium text-[#1C1C1E]">Set up Client Portal Access</p>
                  <p className="text-xs text-[#4B5563]">Create a login now so the client can access their portal immediately</p>
                </div>
              </div>
              <Switch data-testid="client-form-setup-portal-switch" checked={setupPortal} onCheckedChange={setSetupPortal} />
            </div>
            {setupPortal && (
              <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-[#E2E8F0]">
                <div className="space-y-1.5">
                  <Label>Login Email</Label>
                  <Input data-testid="client-form-portal-email-input" type="email" value={portalEmail} onChange={(e) => setPortalEmail(e.target.value)} placeholder="client@company.com" />
                </div>
                <div className="space-y-1.5">
                  <Label>Password</Label>
                  <Input data-testid="client-form-portal-password-input" type="password" value={portalPassword} onChange={(e) => setPortalPassword(e.target.value)} placeholder="••••••••" />
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="client-form-cancel-button">Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="client-form-submit-button" className="bg-[#76C121] hover:bg-[#5C9819] text-white">
            {submitting ? "Saving..." : "Add Client"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
